import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Input, Label } from 'reactstrap';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/dist/sweetalert2.min.css';
import 'sweetalert2/src/sweetalert2.scss';
import erorSound from '../../../assets/audio/alert-error.wav';
import { useAuth } from '../../../auth';
import api from '../../../services/api-configuration';
import useFormatMessage from '../../../utils/useFormatMessage';
import showConfirmationDialog from '../../ui/ConfirmationDialog';
import ChassisSelectionModal from '../chassis-selection-modal/ChassisSelectionModal';

const useBarcodeScanner = (onBarcodeScanned) => {
    const [buffer, setBuffer] = useState("");
    const timeoutRef = useRef(null);
    const sonTusZamaniRef = useRef(Date.now());
    const TARAYICI_ESIK = 50; // Tuşlar arası süre (ms) - Daha toleranslı bir değer
    const lastScannedRef = useRef(""); // Son okutulan barkod
    const lastScannedTimeRef = useRef(0); // Son okutma zamanı
    const DEBOUNCE_TIME = 1500; // Aynı barkod için minimum bekleme süresi (ms) - Artırıldı

    useEffect(() => {
        const tusBasildiginda = (e) => {
            const simdikiZaman = Date.now();
            const zamanFarki = simdikiZaman - sonTusZamaniRef.current;

            // Tuşlar arası süre çok kısaysa muhtemelen tarayıcıdır
            const muhtemelTarayici = zamanFarki < TARAYICI_ESIK;

            if (e.key === "Enter") {
                if (buffer) {
                    // Barkod okuyucudan gelen değer her zaman 17 karakter olmalı
                    // Ancak düşük kaliteli cihazlarda eksik karakter olabilir
                    if (muhtemelTarayici && (buffer.length === 17)) {
                        // Aynı barkod için debounce kontrolü
                        const timeSinceLastScan = simdikiZaman - lastScannedTimeRef.current;
                        if (buffer === lastScannedRef.current && timeSinceLastScan < DEBOUNCE_TIME) {
                            console.log("Duplicate scan prevented", buffer);
                            e.preventDefault();
                            setBuffer("");
                            return;
                        }

                        // Yeni okutma bilgilerini kaydet
                        lastScannedRef.current = buffer;
                        lastScannedTimeRef.current = simdikiZaman;

                        // 16 karakterli şase numaralarını 17 karaktere tamamla (gerekirse)
                        const processedBuffer = buffer.length === 16 ? buffer + "0" : buffer;

                        onBarcodeScanned(processedBuffer);
                        setBuffer("");
                        e.preventDefault(); // Form gönderimini engelle
                    } else if (buffer.length !== 17) {
                        toast.error("Barkod eksik okundu! Tekrar deneyin veya manuel girin.", {
                            autoClose: 10000
                        });
                        e.preventDefault();
                    }
                }
            } else if (e.key && e.key.length === 1) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }

                // Muhtemel tarayıcı girişi ise buffer'a ekle
                if (muhtemelTarayici || buffer.length > 0) { // İlk karakter sonrası daha toleranslı ol
                    setBuffer(prev => prev + e.key);
                }

                timeoutRef.current = setTimeout(() => {
                    setBuffer("");
                }, 200); // Daha uzun bir timeout süresi
            }

            sonTusZamaniRef.current = simdikiZaman;
        };

        window.addEventListener("keydown", tusBasildiginda);
        return () => {
            window.removeEventListener("keydown", tusBasildiginda);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [buffer, onBarcodeScanned]);
};

const SorgulamaEkraniV3 = ({ apiEndpoint, setData, minCharacter, depo, codeee, role_name, title, onChassisUpdate, revert_uri, isRevertMode, process_id }) => {
    const [barcode, setBarcode] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [lastUsedChassis, setLastUsedChassis] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [chassisList, setChassisList] = useState([]);
    const [isBarcodeScanner, setIsBarcodeScanner] = useState(false);
    const inputRef = useRef(null);
    const processingRef = useRef(false); // API isteği işleniyor mu kontrolü
    const lastProcessedChassisRef = useRef(""); // Son işlenen şase
    const lastProcessTimeRef = useRef(0); // Son işlem zamanı
    const { t } = useFormatMessage();
    const { hasRole } = useAuth(7);
    const notifyError = (message) => toast.error(message);

    const handleChassisPatternSearch = async (value) => {
        // Manuel giriş için minimum 8 karakter kontrolü
        if (value.trim().length < 8) {
            notifyError(t("En az 8 karakter giriniz"));
            return;
        }

        try {
            const response = await api.post("/getVehiclesByChassisPattern", {
                chassis_pattern: value.trim().toUpperCase()
            });

            if (response.data.success) {
                if (response.data.count === 0) {
                    notifyError(t("Şase bulunamadı"));
                    return;
                }
                if (response.data.count === 1) {
                    handleApiCall(response.data.data[0]);
                } else if (response.data.count > 1) {
                    setChassisList(response.data.data);
                    setIsModalOpen(true);
                }
            }
        } catch (error) {
            console.error("Error searching chassis pattern:", error);
            notifyError(t("Şase arama sırasında bir hata oluştu"));
        }
    };

    const handleApiCall = async (value) => {
        if (isLoading) return;

        // Eğer zaten işleniyor ise, çift isteği engelle
        if (processingRef.current) {
            console.log("Request already in progress, preventing duplicate call");
            return;
        }

        // Aynı şase için kısa süre içinde tekrar istek kontrolü (500ms)
        const now = Date.now();
        const timeSinceLastProcess = now - lastProcessTimeRef.current;
        if (value === lastProcessedChassisRef.current && timeSinceLastProcess < 500) {
            console.log("Preventing duplicate API call for the same chassis", value);
            return;
        }

        if (role_name && !hasRole(role_name)) {
            toast.error("Bu işlemi yapmaya yetkiniz yoktur.");
            return;
        }

        if (!value || value.trim().length === 0) {
            notifyError(t("Şase No Giriniz"));
            return;
        }

        const trimmedValue = value.trim().toUpperCase();

        // Şase numarası 17 karakter olmalı, ancak 16 karakteri de kabul edelim
        // 16 karakterli şase numaralarını 17 karaktere tamamla
        let processedValue = trimmedValue;
        if (trimmedValue.length === 16) {
            processedValue = trimmedValue + "0"; // 16 karakterli şase numarasını tamamla
            console.log("16 karakterli şase numarası 17 karaktere tamamlandı:", processedValue);
        }

        // Barkod okuyucudan gelen değer her zaman 17 karakter olmalı
        // Manuel giriş için pattern search yapılacak
        if (!isBarcodeScanner && processedValue.length < 16) {
            await handleChassisPatternSearch(processedValue);
            return;
        }

        // İşlem başladı, kilidi aktifleştir
        processingRef.current = true;
        lastProcessedChassisRef.current = processedValue;
        lastProcessTimeRef.current = now;

        setIsLoading(true);
        try {
            let body = {
                chassis_no: processedValue,
                update_user_id: parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'))
            };

            if (document.querySelector("#depo-select-box")) {
                const depoValue = document.querySelector("#depo-select-box").childNodes[0].childNodes[0].value;
                if (depo && !depoValue) {
                    notifyError(t("Lütfen bir depo seçiniz."));
                    processingRef.current = false; // Kilidi kaldır
                    setIsLoading(false);
                    return;
                }
                body.warehouse_code = depoValue;
            }

            if (codeee) {
                body.warehouse_id = parseInt(Buffer.from(localStorage.getItem("warehouse"), 'base64').toString('ascii'));
            }

            // Check if we're in revert mode
            if (isRevertMode) {
                if (!revert_uri) {
                    notifyError(t("Geri alma işlemi için revert_uri tanımlanmamış"));
                    processingRef.current = false;
                    setIsLoading(false);
                    return;
                }

                if (!process_id) {
                    notifyError(t("Geri alma işlemi için process_id bulunamadı"));
                    processingRef.current = false;
                    setIsLoading(false);
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, 400));
                setTimeout(() => {
                    const confirmButton = document.querySelector('.elegant-confirmation-confirm');
                    if (confirmButton) {
                        confirmButton.blur();
                    }
                }, 50);
                // Show confirmation dialog
                const confirmed = await showConfirmationDialog({
                    title: t("Onay"),
                    message: t(`<b>${processedValue}</b> şase numaralı aracın işlemini geri almak istediğinize emin misiniz?`),
                    confirmText: t("Evet"),
                    cancelText: t("Hayır"),
                    t: t
                });

                if (!confirmed) {
                    processingRef.current = false;
                    setIsLoading(false);
                    return;
                }

                const response = await api.post(apiEndpoint, {
                    ...body,
                    process_id: process_id
                });
                if (response.data.message === "PDI data successfully removed") {
                    response.data.sase = processedValue
                }
                setData(response.data);
                setLastUsedChassis(processedValue);
                setBarcode(""); // Her işlem sonrası şase numarasını temizle
            } else {
                // Normal işlem için process_id'yi ekle
                if (process_id) {
                    body.process_id = process_id;
                }
                const response = await api.post(apiEndpoint, body);
                if (response.data.message === "PDI data successfully removed") {
                    response.data.sase = processedValue
                }
                setData(response.data);
                setLastUsedChassis(processedValue);
                setBarcode(""); // Her işlem sonrası şase numarasını temizle
            }
        } catch (error) {
            if (error.response?.data) {
                setData(error.response.data);
                setBarcode(""); // Hata durumunda da şase numarasını temizle
            } else {
                console.error("Beklenmedik bir hata oluştu:", String(error.response));
                if (navigator.vibrate) {
                    navigator.vibrate(500);
                }
                const errorSound = document.getElementById("errorSound");
                errorSound.play();

                Swal.fire({
                    icon: "error",
                    title: t("Hata"),
                    text: t("Beklenmedik bir hata oluştu"),
                    confirmButtonText: t('Tamam'),
                    animation: true,
                    showCancelButton: false,
                    showCloseButton: false,
                    confirmButtonColor: 'rgb(255 84 84)',
                    allowOutsideClick: false,
                });
                setBarcode(""); // Hata durumunda da şase numarasını temizle
            }
        } finally {
            setIsLoading(false);
            setIsBarcodeScanner(false); // Barkod okuyucu modunu sıfırla
            // İşlem tamamlandı, kilidi kaldır
            setTimeout(() => {
                processingRef.current = false;
            }, 300); // Ekstra güvenlik için kısa bir gecikme

            // Input alanına otomatik odaklan
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    };

    // Barkod tarayıcı girişi
    const handleBarcodeScanned = (scannedValue) => {
        if (!isLoading && !processingRef.current) {
            // Barkod okuyucudan gelen değer direkt olarak API'ye gönderilir
            handleApiCall(scannedValue);
        }
    };

    // Barkod tarayıcı hook'unu kur
    useBarcodeScanner(handleBarcodeScanned);

    // Manuel giriş
    const handleManualInputChange = (e) => {
        setBarcode(e.target.value);
    };

    // Manuel gönderim
    const handleButtonClick = () => {
        if (!processingRef.current) {
            handleApiCall(barcode);
        }
    };

    // Modal seçimi
    const handleChassisSelect = (selectedChassis) => {
        setIsModalOpen(false);
        if (!processingRef.current) {
            handleApiCall(selectedChassis);
        }
    };

    return (
        <div className='p-4'>
            <audio id="errorSound" src={erorSound} preload="auto" />
            <div className="form-horizontal well-lg">
                <div className="form-group row">
                    <div className="col-md-4 col-xs-12">
                        <Label
                            className="control-label col-md-3 col-xs-4"
                            htmlFor="Sase"
                            style={{ fontWeight: "600", width: "100%", maxWidth: "100%" }}
                        >
                            {t("Şase")} &nbsp;
                        </Label>
                        <Input
                            ref={inputRef}
                            className="form-control input-sm text-box single-line"
                            id="Entity_Sase"
                            placeholder={t("Şase No Giriniz (min. 8 karakter)")}
                            required
                            type="text"
                            minLength={minCharacter}
                            value={barcode}
                            disabled={isLoading}
                            title="Şase numarası en az 17 karakter olmalıdır."
                            onChange={handleManualInputChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isLoading && !processingRef.current) {
                                    handleApiCall(barcode);
                                }
                            }}
                        />
                        {lastUsedChassis && (
                            <small className="text-muted mt-1 d-block">
                                {t("Son işlem")}: {lastUsedChassis}
                            </small>
                        )}
                        <span
                            className="field-validation-valid text-danger"
                            data-valmsg-for="Entity.Sase"
                            data-valmsg-replace="true"
                        />
                    </div>
                </div>

                <hr />

                <div className="row">
                    <div className="col-md-6 col-xs-12">
                        <Button
                            type="button"
                            className="btn btn-block"
                            id="btnok"
                            color={isRevertMode ? "danger" : "success"}
                            disabled={isLoading || (!role_name ? false : !(hasRole(role_name)))}
                            onClick={handleButtonClick}
                        >
                            <i className={`fa fa-${isRevertMode ? 'undo' : 'check'} text-white`} />&nbsp;&nbsp;
                            {isLoading ? t("İşleniyor...") : isRevertMode ? t("Geri Al") : t("Tamam")}
                        </Button>
                    </div>
                </div>
            </div>

            <ChassisSelectionModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                chassisList={chassisList}
                onSelect={handleChassisSelect}
            />
        </div>
    );
};

export default SorgulamaEkraniV3;